import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Briefcase, Calendar, MapPin, User, LayoutGrid, CalendarDays, Repeat, Lock, Receipt, DollarSign, TrendingUp, Wrench } from "lucide-react";
import { toast } from "sonner";
import JobsCalendar from "@/components/JobsCalendar";
import { CustomerSearchSelect } from "@/components/CustomerSearchSelect";
import { NearbyDaysHint, useNearbyDays } from "@/components/NearbyDaysHint";
import OnMyWayButtons from "@/components/OnMyWayButtons";
import CustomerCommsButtons from "@/components/CustomerCommsButtons";
import { isoToBusinessInput, businessInputToIso, formatBusinessDate } from "@/lib/businessTz";

const JOB_STATUS = {
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  canceled: "bg-rose-100 text-rose-800 border-rose-200",
};

const EMPTY = {
  customer_id: "",
  lead_id: "",
  title: "",
  service_address: "",
  scheduled_start: "",
  scheduled_end: "",
  value: 0,
  assigned_to: "",
  notes: "",
  status: "scheduled",
  photos: [],
  recurrence: null,
};

export default function Jobs() {
  const [items, setItems] = useState([]);
  const [pickedCustomer, setPickedCustomer] = useState(null);
  const [nearbyDays, setNearbyDays] = useState(null);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("all");
  const [viewMode, setViewMode] = useState("calendar"); // grid | calendar — defaults to calendar per user spec
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [lockedCustomer, setLockedCustomer] = useState(null);
  const [lockedLead, setLockedLead] = useState(null);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();

  const launchCustomerId = params.get("customer_id") || "";
  const launchLeadId = params.get("lead_id") || "";
  const isLocked = !!(lockedCustomer || lockedLead);

  const load = async () => {
    const [{ data: j }, sumRes] = await Promise.all([
      api.get("/jobs"),
      api.get("/jobs/summary").catch(() => ({ data: null })),
    ]);
    setItems(j);
    if (sumRes?.data) setSummary(sumRes.data);
  };
  useEffect(() => { load(); }, []);

  // Auto-open locked dialog when navigated from a customer/lead profile
  useEffect(() => {
    const openNew = async () => {
      if (launchCustomerId) {
        try {
          const { data: c } = await api.get(`/customers/${launchCustomerId}`);
          setLockedCustomer(c);
          setLockedLead(null);
          setForm({ ...EMPTY, customer_id: c.id, service_address: c.address || "" });
          setOpen(true);
        } catch { toast.error("Customer not found"); }
      } else if (launchLeadId) {
        try {
          const { data: l } = await api.get(`/leads/${launchLeadId}`);
          setLockedLead(l);
          let cid = l.customer_id || "";
          if (cid) {
            try { const { data: c } = await api.get(`/customers/${cid}`); setLockedCustomer(c); } catch {}
          }
          setForm({ ...EMPTY, customer_id: cid, lead_id: l.id, service_address: l.property_address || "", title: l.service_requested || "" });
          setOpen(true);
        } catch { toast.error("Lead not found"); }
      }
    };
    if (launchCustomerId || launchLeadId) openNew();
    // eslint-disable-next-line
  }, [launchCustomerId, launchLeadId]);

  const closeDialog = () => {
    setOpen(false);
    setLockedCustomer(null);
    setLockedLead(null);
    setPickedCustomer(null);
    setForm(EMPTY);
    if (launchCustomerId || launchLeadId) {
      // Strip query params so the picker returns on next fresh visit
      setParams({});
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Locked mode: at least one of customer/lead must be present.
    if (!isLocked && !form.customer_id) { toast.error("Pick a customer"); return; }
    if (isLocked && !form.customer_id && !form.lead_id) { toast.error("Missing profile context"); return; }
    try {
      const payload = {
        ...form,
        value: Number(form.value)||0,
        // Interpret datetime-local inputs as Chicago wall-clock → UTC ISO
        scheduled_start: form.scheduled_start ? businessInputToIso(form.scheduled_start) : null,
        scheduled_end: form.scheduled_end ? businessInputToIso(form.scheduled_end) : null,
      };
      // If lead-locked with no customer yet, the backend will still expect
      // customer_id — pre-materialize by creating a customer from the lead
      // via the profile-launched estimate helper; simplest: prompt user.
      if (!payload.customer_id && payload.lead_id && lockedLead) {
        // Create a lightweight customer inline first
        const { data: newCust } = await api.post("/customers", {
          name: lockedLead.customer_name,
          email: lockedLead.email || "",
          phone: lockedLead.phone || "",
          address: lockedLead.property_address || "",
          notes: `Auto-created from lead ${lockedLead.customer_name}`,
        });
        payload.customer_id = newCust.id;
      }
      const { data } = await api.post("/jobs", payload);
      toast.success("Job created");
      closeDialog();
      load();
      nav(`/app/jobs/${data.id}`);
    } catch { toast.error("Failed"); }
  };

  const filtered = items.filter((j) => status === "all" ? true : j.status === status);

  // Iter32 — prominent "Create invoice" on a completed job. Reuses the
  // existing estimate→invoice pipeline when the job originated from an
  // estimate (which is the common path from Iter32 Schedule Job). If the
  // job was created ad-hoc without an estimate, we fall back to a direct
  // POST /api/invoices seeded from the job's totals.
  const createInvoiceFromJob = async (job) => {
    if (!window.confirm(`Create an invoice from ${job.title || "this job"}?`)) return;
    try {
      let inv;
      if (job.estimate_id) {
        const { data } = await api.post(`/estimates/${job.estimate_id}/create-invoice`);
        inv = data;
      } else {
        const { data } = await api.post(`/invoices`, {
          customer_id: job.customer_id,
          job_id: job.id,
          title: job.title,
          line_items: [{ name: job.title || "Service", quantity: 1, unit_price: job.value || 0, taxable: true }],
          discount_type: "percent", discount_value: 0, tax_rate: null, notes: job.notes || "",
        });
        inv = data;
      }
      toast.success(`Invoice ${inv.invoice_number} created`);
      // Reflect invoice_id on the local job so the button flips to "View invoice"
      setItems(prev => prev.map(x => x.id === job.id ? { ...x, invoice_id: inv.id } : x));
      nav(`/app/invoices/${inv.id}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "";
      toast.error(`Create invoice failed${status ? ` (${status})` : ""}: ${detail}`);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Delivery</div>
          <h1 className="font-heading text-4xl font-bold">Jobs</h1>
        </div>
        <Button onClick={()=>{setForm(EMPTY); setOpen(true);}} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="jobs-new-btn">
          <Plus className="w-4 h-4 mr-1"/> New job
        </Button>
      </div>

      {/* Month KPIs — paid invoices, work-in-progress, scheduled $ this month */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="jobs-summary-row">
        <KpiCard
          testid="jobs-kpi-paid"
          label={summary ? `Paid invoices · ${summary.month_label}` : "Paid invoices · this month"}
          value={money(summary?.paid_invoices?.total)}
          hint={summary ? `${summary.paid_invoices.count} invoice${summary.paid_invoices.count === 1 ? "" : "s"} collected` : "—"}
          icon={<DollarSign className="w-4 h-4 text-emerald-700"/>}
          accent="bg-emerald-50 border-emerald-200"
          iconWrap="bg-emerald-100"
        />
        <KpiCard
          testid="jobs-kpi-wip"
          label="Work in progress"
          value={money(summary?.work_in_progress?.total)}
          hint={summary ? `${summary.work_in_progress.count} job${summary.work_in_progress.count === 1 ? "" : "s"} on the truck now` : "—"}
          icon={<Wrench className="w-4 h-4 text-amber-700"/>}
          accent="bg-amber-50 border-amber-200"
          iconWrap="bg-amber-100"
        />
        <KpiCard
          testid="jobs-kpi-scheduled"
          label={summary ? `Scheduled · ${summary.month_label}` : "Scheduled · this month"}
          value={money(summary?.scheduled_this_month?.total)}
          hint={summary ? `${summary.scheduled_this_month.count} job${summary.scheduled_this_month.count === 1 ? "" : "s"} on the calendar` : "—"}
          icon={<TrendingUp className="w-4 h-4 text-blue-700"/>}
          accent="bg-blue-50 border-blue-200"
          iconWrap="bg-blue-100"
        />
      </div>

      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {["all","scheduled","in_progress","completed","canceled"].map((s) => (
            <button key={s} onClick={()=>setStatus(s)} className={`text-xs px-3 py-1.5 rounded-md border capitalize ${status===s?"bg-zinc-900 text-white border-zinc-900":"bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`} data-testid={`jobs-filter-${s}`}>{s.replace(/_/g," ")}</button>
          ))}
        </div>
        <div className="flex gap-1">
          <button onClick={()=>setViewMode("grid")} className={`text-xs px-3 py-1.5 rounded-md border flex items-center gap-1 ${viewMode==="grid"?"bg-zinc-900 text-white border-zinc-900":"bg-white text-zinc-700 border-zinc-200"}`} data-testid="jobs-view-grid"><LayoutGrid className="w-3.5 h-3.5"/> Grid</button>
          <button onClick={()=>setViewMode("calendar")} className={`text-xs px-3 py-1.5 rounded-md border flex items-center gap-1 ${viewMode==="calendar"?"bg-zinc-900 text-white border-zinc-900":"bg-white text-zinc-700 border-zinc-200"}`} data-testid="jobs-view-calendar"><CalendarDays className="w-3.5 h-3.5"/> Calendar</button>
        </div>
      </div>

      {viewMode === "calendar" ? (
        <>
          <CalendarNearbyPicker onDays={setNearbyDays} />
          <JobsCalendar jobs={filtered} onJobsChanged={load} highlightDays={nearbyDays}/>
        </>
      ) : (
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((j) => (
          <div key={j.id} className="bg-white border border-zinc-200 rounded-xl p-5 card-crisp hover:-translate-y-0.5 transition-transform" data-testid={`job-card-${j.id}`}>
            <button onClick={()=>nav(`/app/jobs/${j.id}`)} className="text-left w-full">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-[#FF5A00]"/>
                </div>
                <span className={`text-xs border rounded px-2 py-0.5 capitalize ${JOB_STATUS[j.status]}`}>{j.status.replace(/_/g," ")}</span>
              </div>
              <div className="font-heading font-bold text-lg truncate">{j.title}</div>
              <div className="text-sm text-zinc-600 mt-1 truncate flex items-center gap-1"><User className="w-3.5 h-3.5"/>{j.customer_name || "—"}</div>
              {j.service_address && <div className="text-xs text-zinc-500 truncate mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{j.service_address}</div>}
              <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between">
                <div className="flex items-center gap-1 text-xs text-zinc-500">
                  <Calendar className="w-3.5 h-3.5"/>
                  {j.scheduled_start ? formatBusinessDate(j.scheduled_start) : "Unscheduled"}
                  {j.recurrence && <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono-accent uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-200 rounded"><Repeat className="w-2.5 h-2.5"/> {j.recurrence.frequency}</span>}
                </div>
                <div className="font-mono-accent font-bold">${(j.value||0).toLocaleString()}</div>
              </div>
            </button>
            {/* On-My-Way quick texts (Phase 4). Only for live jobs, not cancelled/completed. */}
            {j.status !== "completed" && j.status !== "canceled" && (
              <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between gap-2 flex-wrap">
                <OnMyWayButtons job={j} />
                {j.customer_id && (
                  <CustomerCommsButtons
                    customer={{
                      id: j.customer_id,
                      phone: j.customer_phone,
                      email: j.customer_email,
                      sms_opted_out: j.customer_sms_opted_out,
                    }}
                    jobId={j.id}
                    rowTestId={`job-card-${j.id}`}
                  />
                )}
              </div>
            )}
            {/* Iter32 — prominent "Create invoice" when the job is completed
                and (a) has no linked invoice yet, or (b) has a linked one
                (button flips to "View invoice" for parity with Estimates). */}
            {j.status === "completed" && (
              <div className="mt-3 pt-3 border-t border-zinc-100">
                {j.invoice_id ? (
                  <Button size="sm" variant="outline" className="w-full" onClick={()=>nav(`/app/invoices/${j.invoice_id}`)} data-testid={`job-view-invoice-${j.id}`}>
                    <Receipt className="w-3.5 h-3.5 mr-1"/> View invoice
                  </Button>
                ) : (
                  <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={()=>createInvoiceFromJob(j)} data-testid={`job-create-invoice-${j.id}`}>
                    <Receipt className="w-3.5 h-3.5 mr-1"/> Create invoice
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 bg-white border border-dashed border-zinc-300 rounded-xl">
            <Briefcase className="w-8 h-8 text-zinc-300 mx-auto mb-2"/>
            <div className="text-zinc-500">No jobs {status !== "all" ? `in ${status.replace(/_/g," ")}` : "yet"}.</div>
          </div>
        )}
      </div>
      )}

      <Dialog open={open} onOpenChange={(v)=>{ if (!v) closeDialog(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="job-form-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New job</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Customer</Label>
              {isLocked ? (
                <div
                  className="border border-zinc-200 rounded-lg bg-zinc-50 p-3 text-sm"
                  data-testid="job-form-customer-locked"
                  data-customer-id={lockedCustomer?.id || ""}
                  data-lead-id={lockedLead?.id || ""}
                  data-origin={lockedLead ? "lead" : "customer"}
                >
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#FF5A00]"/>
                    <div className="font-medium truncate">{lockedCustomer?.name || lockedLead?.customer_name || "Profile"}</div>
                    <span className="ml-auto text-[10px] uppercase tracking-widest font-mono-accent text-zinc-500 flex items-center gap-1">
                      <Lock className="w-3 h-3"/> Locked · From {lockedLead ? "lead" : "customer"}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 truncate mt-1">
                    {[lockedCustomer?.email || lockedLead?.email, lockedCustomer?.phone || lockedLead?.phone].filter(Boolean).join(" · ")}
                  </div>
                </div>
              ) : (
                <CustomerSearchSelect
                  selected={pickedCustomer}
                  onSelect={(c)=>{ setPickedCustomer(c); setForm((f)=>({...f, customer_id: c.id, service_address: f.service_address || c.address || ""})); }}
                  testid="job-form-customer"
                />
              )}
            </div>
            <div>
              <Label>Title</Label>
              <Input required value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} data-testid="job-form-title"/>
            </div>
            <div>
              <Label>Service address</Label>
              <Input value={form.service_address} onChange={(e)=>setForm({...form, service_address: e.target.value})} data-testid="job-form-address"/>
            </div>
            <NearbyDaysHint
              customerId={form.customer_id || lockedCustomer?.id}
              address={form.service_address}
              onPick={(ymd)=>setForm((f)=>({...f, scheduled_start: `${ymd}T09:00`, scheduled_end: f.scheduled_end ? `${ymd}T10:00` : f.scheduled_end}))}
              testid="job-form-nearby"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Scheduled start <span className="text-[10px] font-mono-accent text-zinc-500">(CT)</span></Label>
                <Input type="datetime-local" value={toFormInput(form.scheduled_start)} onChange={(e)=>setForm({...form, scheduled_start: e.target.value})} data-testid="job-form-start"/>
              </div>
              <div>
                <Label>Scheduled end <span className="text-[10px] font-mono-accent text-zinc-500">(CT)</span></Label>
                <Input type="datetime-local" value={toFormInput(form.scheduled_end)} onChange={(e)=>setForm({...form, scheduled_end: e.target.value})}/>
              </div>
              <div>
                <Label>Value ($)</Label>
                <Input type="number" step="0.01" value={form.value} onChange={(e)=>setForm({...form, value: e.target.value})} data-testid="job-form-value"/>
              </div>
              <div>
                <Label>Assigned to</Label>
                <Input value={form.assigned_to} onChange={(e)=>setForm({...form, assigned_to: e.target.value})} placeholder="Team member name" data-testid="job-form-assigned"/>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}/>
            </div>
            <div className="border-t border-zinc-200 pt-3">
              <label className="flex items-center gap-2 text-sm mb-2">
                <input type="checkbox" checked={!!form.recurrence} onChange={(e)=>setForm({...form, recurrence: e.target.checked ? { frequency: "weekly", interval: 1, until: null } : null})} data-testid="job-form-recurring-toggle"/>
                <Repeat className="w-3.5 h-3.5 text-indigo-600"/>
                <span>Repeat this job on a schedule</span>
              </label>
              {form.recurrence && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Frequency</Label>
                    <Select value={form.recurrence.frequency} onValueChange={(v)=>setForm({...form, recurrence: {...form.recurrence, frequency: v}})}>
                      <SelectTrigger data-testid="job-form-recurrence-frequency"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Interval (every N)</Label>
                    <Input type="number" min="1" value={form.recurrence.interval} onChange={(e)=>setForm({...form, recurrence: {...form.recurrence, interval: Number(e.target.value)||1}})}/>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button type="submit" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="job-form-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Raw "YYYY-MM-DDTHH:mm" form values are already Central wall-clock; only convert stored ISO instants.
function toFormInput(v) {
  if (!v) return "";
  return /(Z|[+-]\d\d:\d\d)$/.test(v) ? (isoToBusinessInput(v) || v) : v;
}

function money(n) {
  const v = Number(n ?? 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function KpiCard({ label, value, hint, icon, accent, iconWrap, testid }) {
  return (
    <div className={`border ${accent} rounded-xl p-4 flex items-start gap-3`} data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg ${iconWrap} flex items-center justify-center shrink-0`}>{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-600 truncate">{label}</div>
        <div className="font-heading text-2xl font-bold leading-tight mt-0.5" data-testid={`${testid}-value`}>{value}</div>
        <div className="text-xs text-zinc-500 mt-0.5 truncate">{hint}</div>
      </div>
    </div>
  );
}

function CalendarNearbyPicker({ onDays }) {
  const [picked, setPicked] = useState(null);
  const cid = picked?.id || "";
  const [addr, setAddr] = useState("");
  const { loading, data } = useNearbyDays(cid, addr);
  useEffect(() => {
    onDays(data?.recommended_days?.length ? Object.fromEntries(data.recommended_days.map((d) => [d.date, d])) : null);
  }, [data, onDays]);
  const clear = () => { setPicked(null); setAddr(""); };
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3 rounded-lg border border-zinc-200 bg-white p-3" data-testid="calendar-nearby-picker">
      <span className="text-xs font-mono-accent uppercase tracking-widest text-emerald-700 flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/> Nearby days for</span>
      <div className="w-60"><CustomerSearchSelect selected={picked} onSelect={(c)=>{ setPicked(c); setAddr(""); }} placeholder="Search a customer" className="h-8 text-xs" testid="calendar-nearby-customer"/></div>
      <span className="text-xs text-zinc-400">or</span>
      <Input value={addr} onChange={(e)=>{ setAddr(e.target.value); setPicked(null); }} placeholder="Type an address" className="w-64 h-8 text-xs" data-testid="calendar-nearby-address"/>
      {(cid || addr) && <button onClick={clear} className="text-xs text-zinc-500 hover:text-zinc-900" data-testid="calendar-nearby-clear">Clear</button>}
      <span className="text-xs text-zinc-500 ml-auto" data-testid="calendar-nearby-status">
        {loading ? "Finding…" : data ? (!data.located ? "No map location for that address" : data.recommended_days.length ? `${data.recommended_days.length} nearby day${data.recommended_days.length === 1 ? "" : "s"} highlighted` : "No nearby booked jobs") : ""}
      </span>
    </div>
  );
}
